// apps/client/src/hooks/useEntityTags.ts
//
// Lead and client tags (eDM plan-v3 core, Slice E3; `edm_content` only).
// Tags live in the server sidecar `edm_tag_links` and are read and written
// through the edmTagAction callable:
//   listRegistry {}                   -> { tags: registry entries }
//   getTags      {kind, ids <= 100}   -> { tags: { [id]: string[] } } (in-scope ids only)
//   bulkTag      {kind, entityIds <= 500, tag, op: 'add' | 'remove'}
//                -> { updated, unchanged, capped, failed, omitted }
// Registry names are already normalised (the registry doc id is the
// nameNorm), so chips, the filter and the bulk bar all show the same strings.
//
// The server is the authority on who may tag what. The predicates below only
// hide controls the server would refuse; out-of-scope ids are silently
// omitted server-side, so the UI never relies on them for access.
//
// Nothing here runs unless the caller passes `enabled: true`, so with the
// flag off the page makes no edmTagAction call at all.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { EdmTagEntry } from './useEdmContacts';
import { edmErrorMessage } from '../utils/edm';

export type EntityTagKind = 'lead' | 'client';

/** Server ceilings (edmTagAction zod) mirrored so no call is refused for size. */
export const ENTITY_TAGS_GET_MAX_IDS = 100;
export const ENTITY_TAGS_BULK_MAX_IDS = 500;

/**
 * Copies of edmTagAction's LEAD_ROLES / CLIENT_ROLES
 * (services/client-functions/src/edm/edmTagAction.ts). The client bundle
 * cannot import service code; the server remains the authority, so a drift
 * here can only hide or show a control, never grant access.
 *
 * Leads: the updateLead role set; broker-filtered admins are refused
 * (enforceAuthenticated default-deny).
 */
const LEAD_TAG_ROLES: ReadonlySet<string> = new Set([
  'super_admin',
  'administrator',
  'office_admin',
  'broker',
  'broker_assistant',
  'credit_analyst',
  'service_staff',
  'marketing',
]);

/** Clients: everyone who can maintain a client record, minus service_staff and marketing. */
const CLIENT_TAG_ROLES: ReadonlySet<string> = new Set([
  'super_admin',
  'administrator',
  'office_admin',
  'broker',
  'broker_assistant',
  'credit_analyst',
]);

export function canTagLeads(role: string | null | undefined, isBrokerFilteredAdmin: boolean): boolean {
  return !!role && LEAD_TAG_ROLES.has(role) && !isBrokerFilteredAdmin;
}

export function canTagClients(role: string | null | undefined): boolean {
  return !!role && CLIENT_TAG_ROLES.has(role);
}

/** Server counts summed over every chunk sent, plus the first failure. */
export interface BulkTagResult {
  updated: number;
  /** Already had the tag (add) or never had it (remove). */
  unchanged: number;
  /** At the 20-tag limit (add only). */
  capped: number;
  /** Write failed on the server. */
  failed: number;
  /** Not in the caller's scope (silently skipped by the server). */
  omitted: number;
  /** Ids in chunks never applied because an earlier chunk's call failed. */
  notSent: number;
  /** The call failure that stopped the run, or null when every chunk was applied. */
  error: unknown;
}

/**
 * `null` = the server returned no tags for this id because it is not in the
 * caller's tagging scope (for clients: visible but not editable). `undefined`
 * (absent) = not loaded yet.
 */
export type EntityTagsById = Readonly<Record<string, string[] | null>>;

export interface UseEntityTagsResult {
  /** Registry entries (null until loaded or when disabled). */
  registry: EdmTagEntry[] | null;
  registryError: unknown;
  tagsById: EntityTagsById;
  /** A getTags failure that is not yet recovered (cleared once those ids load). */
  tagsError: unknown;
  /** Never throws: a call failure is returned in `error` with the counts so far. */
  bulkTag: (entityIds: readonly string[], tag: string, op: 'add' | 'remove') => Promise<BulkTagResult>;
  /** Re-read failed tag reads still on screen and a failed registry load. */
  retry: () => void;
}

interface Params {
  companyId: string | undefined;
  kind: EntityTagKind;
  enabled: boolean;
  /** Entity ids on screen, in display order. */
  ids: readonly string[];
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

async function call<Res>(payload: Record<string, unknown>): Promise<Res> {
  const fn = httpsCallable<Record<string, unknown>, Res>(functions, 'edmTagAction');
  const res = await fn(payload);
  return res.data;
}

function countOf(data: unknown, key: string): number {
  const v = ((data ?? {}) as Record<string, unknown>)[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function useEntityTags({ companyId, kind, enabled, ids }: Params): UseEntityTagsResult {
  const [registry, setRegistry] = useState<EdmTagEntry[] | null>(null);
  const [registryError, setRegistryError] = useState<unknown>(null);
  const [tagsById, setTagsById] = useState<Record<string, string[] | null>>({});
  const [tagsError, setTagsError] = useState<unknown>(null);

  // Ids requested (or in flight) for the current scope, so a "Load more" only
  // fetches the new page. `failed` holds ids whose last read failed; they are
  // retried with the next id change and the error stays shown until they
  // load. `gen` orders reads per id: only the newest read of an id may write
  // it, so a slow page-load reply cannot overwrite the re-read after a bulk
  // write. `scopeRef` discards replies that arrive after the scope changed.
  const requested = useRef<Set<string>>(new Set());
  const failed = useRef<Set<string>>(new Set());
  const gen = useRef<Map<string, number>>(new Map());
  const seq = useRef(0);
  const scopeKey = enabled && companyId ? `${companyId}|${kind}` : '';
  const scopeRef = useRef(scopeKey);

  const loadRegistry = useCallback(
    (scope: string) => {
      setRegistryError(null);
      call<{ tags?: EdmTagEntry[] }>({ action: 'listRegistry', companyId })
        .then((data) => {
          if (scopeRef.current === scope) setRegistry(Array.isArray(data?.tags) ? data.tags : []);
        })
        .catch((err) => {
          if (scopeRef.current === scope) setRegistryError(err);
        });
    },
    [companyId],
  );

  useEffect(() => {
    scopeRef.current = scopeKey;
    requested.current = new Set();
    failed.current = new Set();
    gen.current = new Map();
    setTagsById({});
    setTagsError(null);
    setRegistry(null);
    setRegistryError(null);
    if (!scopeKey) return;
    loadRegistry(scopeKey);
    // companyId and kind are folded into scopeKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // `scope` is the scope the ids belong to; nothing is read or written once
  // the live scope differs. Chunks run in parallel; `gen` keeps per-id order.
  const fetchTags = useCallback(
    async (wanted: readonly string[], scope: string) => {
      if (!scope || scopeRef.current !== scope || wanted.length === 0) return;
      const readChunk = async (part: string[]) => {
        const mine = ++seq.current;
        for (const id of part) gen.current.set(id, mine);
        try {
          const data = await call<{ tags?: Record<string, unknown> }>({
            action: 'getTags',
            companyId,
            kind,
            ids: part,
          });
          if (scopeRef.current !== scope) return;
          const got = data?.tags && typeof data.tags === 'object' ? data.tags : {};
          const current = part.filter((id) => gen.current.get(id) === mine);
          for (const id of current) failed.current.delete(id);
          setTagsById((prev) => {
            const next = { ...prev };
            for (const id of current) {
              const v = got[id];
              next[id] = Array.isArray(v) ? v.filter((t): t is string => typeof t === 'string') : null;
            }
            return next;
          });
          if (failed.current.size === 0) setTagsError(null);
        } catch (err) {
          if (scopeRef.current !== scope) return;
          for (const id of part) {
            if (gen.current.get(id) !== mine) continue;
            requested.current.delete(id);
            failed.current.add(id);
          }
          setTagsError(err);
        }
      };
      await Promise.all(chunk([...new Set(wanted)], ENTITY_TAGS_GET_MAX_IDS).map(readChunk));
    },
    [companyId, kind],
  );

  // Stable dependency for the id list (pages pass a fresh array per render).
  const idsKey = useMemo(() => ids.join('\u0000'), [ids]);

  useEffect(() => {
    if (!scopeKey) return;
    // A failed id that left the screen no longer holds the error banner up.
    const onScreen = new Set(ids);
    for (const id of [...failed.current]) if (!onScreen.has(id)) failed.current.delete(id);
    if (failed.current.size === 0) setTagsError(null);
    const fresh = ids.filter((id) => !requested.current.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) requested.current.add(id);
    void fetchTags(fresh, scopeKey);
    // idsKey stands in for ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, idsKey, fetchTags]);

  const bulkTag = useCallback(
    async (entityIds: readonly string[], tag: string, op: 'add' | 'remove'): Promise<BulkTagResult> => {
      const out: BulkTagResult = { updated: 0, unchanged: 0, capped: 0, failed: 0, omitted: 0, notSent: 0, error: null };
      const scope = scopeRef.current;
      if (!scope) return { ...out, error: new Error('entity tags are not enabled') };
      const parts = chunk(entityIds, ENTITY_TAGS_BULK_MAX_IDS);
      for (let i = 0; i < parts.length; i += 1) {
        try {
          const data = await call<unknown>({ action: 'bulkTag', companyId, kind, entityIds: parts[i], tag, op });
          out.updated += countOf(data, 'updated');
          out.unchanged += countOf(data, 'unchanged');
          out.capped += countOf(data, 'capped');
          out.failed += countOf(data, 'failed');
          out.omitted += countOf(data, 'omitted');
        } catch (err) {
          out.error = err;
          out.notSent = parts.slice(i).reduce((n, p) => n + p.length, 0);
          break;
        }
      }
      // Re-read what the server now holds, including after a partial failure
      // (skipped when the scope changed meanwhile).
      await fetchTags(entityIds, scope);
      return out;
    },
    [companyId, kind, fetchTags],
  );

  /** Re-read failed ids still on screen, and the registry if it failed. */
  const retry = useCallback(() => {
    const scope = scopeRef.current;
    if (!scope) return;
    if (registryError) loadRegistry(scope);
    const again = [...failed.current];
    for (const id of again) requested.current.add(id);
    void fetchTags(again, scope);
  }, [registryError, loadRegistry, fetchTags]);

  return useMemo(
    () => ({ registry, registryError, tagsById, tagsError, bulkTag, retry }),
    [registry, registryError, tagsById, tagsError, bulkTag, retry],
  );
}

/** One sentence per non-zero count, for the notice after a bulk tag. */
export function bulkTagSummary(r: BulkTagResult, op: 'add' | 'remove', noun: { one: string; many: string }): string {
  const n = (k: number) => `${k.toLocaleString('en-AU')} ${k === 1 ? noun.one : noun.many}`;
  const parts = [op === 'add' ? `Tag added to ${n(r.updated)}.` : `Tag removed from ${n(r.updated)}.`];
  if (r.unchanged > 0) parts.push(op === 'add' ? `${n(r.unchanged)} already had it.` : `${n(r.unchanged)} did not have it.`);
  if (r.capped > 0) parts.push(`${n(r.capped)} already ${r.capped === 1 ? 'has' : 'have'} 20 tags; remove one first.`);
  if (r.failed > 0) parts.push(`${n(r.failed)} could not be saved; try again for those.`);
  if (r.omitted > 0) parts.push(`${n(r.omitted)} skipped (not yours to tag).`);
  return parts.join(' ');
}

export interface UseTagSelectionResult {
  selected: ReadonlySet<string>;
  toggle: (id: string) => void;
  allSelected: boolean;
  toggleAll: () => void;
  clear: () => void;
  busy: boolean;
  notice: string | null;
  error: string | null;
  /** BulkTagBar's onApply: one tag, added or removed, on every selected id. */
  apply: (add: string[], remove: string[]) => Promise<void>;
}

/**
 * Row selection + bulk apply shared by LeadsPage and ClientDatabasePage.
 * `visibleIds` are the selectable ids on screen (callers leave out rows the
 * server reported as outside their tagging scope); selection is pruned to
 * them. `onApplied` runs after any write reached the server (for a list
 * refetch under an active tag filter). A new selection clears the previous
 * result notice.
 */
export function useTagSelection(
  visibleIds: readonly string[],
  bulkTag: UseEntityTagsResult['bulkTag'],
  noun: { one: string; many: string },
  onApplied?: () => void,
): UseTagSelectionResult {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const idsKey = useMemo(() => visibleIds.join('\u0000'), [visibleIds]);
  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(visibleIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // idsKey stands in for visibleIds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const toggle = useCallback((id: string) => {
    setNotice(null);
    setError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleAll = useCallback(() => {
    setNotice(null);
    setError(null);
    setSelected(allSelected ? new Set() : new Set(visibleIds));
  }, [allSelected, visibleIds]);
  const clear = useCallback(() => setSelected(new Set()), []);

  const { one, many } = noun;
  const apply = useCallback(
    async (add: string[], remove: string[]) => {
      const op: 'add' | 'remove' = add.length > 0 ? 'add' : 'remove';
      const tag = op === 'add' ? add[0] : remove[0];
      if (!tag) return;
      setBusy(true);
      setNotice(null);
      setError(null);
      try {
        const r = await bulkTag([...selected], tag, op);
        const total = r.updated + r.unchanged + r.capped + r.failed + r.omitted;
        if (total > 0) setNotice(bulkTagSummary(r, op, { one, many }));
        if (r.error) {
          const notSent = r.notSent > 0 ? ` ${r.notSent.toLocaleString('en-AU')} ${r.notSent === 1 ? one : many} not sent.` : '';
          setError(`${edmErrorMessage(r.error)}${notSent}`);
        } else {
          setSelected(new Set());
        }
        if (r.updated > 0) onApplied?.();
      } finally {
        setBusy(false);
      }
    },
    [bulkTag, selected, one, many, onApplied],
  );

  return { selected, toggle, allSelected, toggleAll, clear, busy, notice, error, apply };
}
